import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CreditCard, DollarSign, Send, Loader, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Purchase = () => {
  const { widgetId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [error, setError] = useState('');
  const [manualSuccess, setManualSuccess] = useState(false);

  const fetchWidget = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/widgets/${widgetId}`);
      setWidget(data);
    } catch (err) {
      setError('Widget not found');
    } finally {
      setLoading(false);
    }
  }, [widgetId]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else {
      fetchWidget();
    }
  }, [widgetId, user, authLoading, navigate, fetchWidget]);

  const handlePurchase = async () => {
    setError('');
    setProcessing(true);
    setManualSuccess(false);

    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${API}/purchase`,
        {
          widget_id: widgetId,
          payment_method: selectedMethod,
          origin_url: originUrl,
        },
        { withCredentials: true }
      );

      if (selectedMethod === 'stripe' && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (selectedMethod === 'manual') {
        setManualSuccess(true);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Purchase failed. Please try again.');
      setProcessing(false);
    }
  };

  const getSubmitLabel = () => {
    if (processing) return 'Processing...';
    if (selectedMethod === 'manual') return 'Submit Request';
    return 'Proceed to Payment';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center" data-testid="purchase-loading">
        <Loader className="w-8 h-8 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  if (error && !widget) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Widget Not Found</h2>
          <a href="/#widgets" className="text-[#39FF14] hover:text-[#2AE00F]">
            Browse Widgets
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] py-12 px-4 sm:px-6 lg:px-8 bg-[#0F1113]" data-testid="purchase-page">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="aspect-video bg-[#0C0C12] rounded-sm overflow-hidden mb-6">
              <img
                src={widget.image_url}
                alt={widget.name}
                className="w-full h-full object-cover"
                data-testid="purchase-widget-image"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="purchase-widget-name">
              {widget.name}
            </h1>
            <p className="text-base text-[#A1A1AA] mb-6" data-testid="purchase-widget-description">
              {widget.description}
            </p>
            <ul className="space-y-2">
              {widget.features.map((feature) => (
                <li key={feature} className="flex items-center space-x-2 text-sm text-[#A1A1AA]">
                  <div className="w-1 h-1 bg-[#39FF14] rounded-full"></div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-sm p-8">
              <h2 className="text-xl font-bold mb-6">Complete Purchase</h2>

              {manualSuccess ? (
                <div className="text-center py-8" data-testid="purchase-manual-success">
                  <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Request Submitted!</h3>
                  <p className="text-[#A1A1AA] mb-6">
                    We've received your manual payment request. Our team will contact you shortly with payment
                    instructions.
                  </p>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-[#39FF14] text-black px-6 py-3 font-bold hover:bg-[#2AE00F] rounded-sm"
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[#A1A1AA]">Price</span>
                      <span className="text-3xl font-bold text-[#39FF14]" data-testid="purchase-widget-price">
                        ${widget.price}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-4">Select Payment Method</label>
                    <div className="space-y-3">
                      <button
                        onClick={() => setSelectedMethod('stripe')}
                        className={`w-full p-4 rounded-sm border transition-all flex items-center space-x-3 ${
                          selectedMethod === 'stripe'
                            ? 'border-[#39FF14] bg-[#39FF14]/10'
                            : 'border-white/10 bg-[#0C0C12] hover:border-white/20'
                        }`}
                        data-testid="payment-method-stripe"
                      >
                        <CreditCard className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium">Credit Card (Stripe)</div>
                          <div className="text-xs text-[#A1A1AA]">Instant access</div>
                        </div>
                      </button>

                      <button
                        onClick={() => setSelectedMethod('manual')}
                        className={`w-full p-4 rounded-sm border transition-all flex items-center space-x-3 ${
                          selectedMethod === 'manual'
                            ? 'border-[#39FF14] bg-[#39FF14]/10'
                            : 'border-white/10 bg-[#0C0C12] hover:border-white/20'
                        }`}
                        data-testid="payment-method-manual"
                      >
                        <Send className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium">Manual / Contact</div>
                          <div className="text-xs text-[#A1A1AA]">We'll contact you</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm" data-testid="purchase-error-message">
                      <span className="text-red-400 text-sm">{error}</span>
                    </div>
                  )}

                  <button
                    onClick={handlePurchase}
                    disabled={processing}
                    className="w-full bg-[#39FF14] text-black px-6 py-4 font-bold hover:bg-[#2AE00F] hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all rounded-sm inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="purchase-submit-button"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span>{getSubmitLabel()}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};